import { useEffect, useState } from "react";
import { Loader2, TrendingUp, Globe2, Calendar, Eye, Compass } from "lucide-react";
import { adminAnalytics } from "@/lib/api";

const StatCard = ({ label, value, hint, testid }) => (
  <div
    data-testid={testid}
    className="glass-card rounded-2xl p-5"
  >
    <div className="label-overline text-ash/70">{label}</div>
    <div className="mt-2 font-serif-display text-pearl text-3xl sm:text-4xl leading-none">
      {value.toLocaleString()}
    </div>
    {hint && (
      <div className="mt-2 text-[11px] text-ash/70 font-mono-label tracking-wide">
        {hint}
      </div>
    )}
  </div>
);

const SignupChart = ({ daily }) => {
  if (!daily?.length) return null;
  const max = Math.max(1, ...daily.map((d) => d.count));
  const W = 720;
  const H = 120;
  const pad = 8;
  const barW = (W - pad * 2) / daily.length;

  return (
    <div data-testid="admin-signups-chart" className="glass-card rounded-2xl p-5">
      <div className="flex items-baseline justify-between mb-4">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-3.5 h-3.5 text-bone" />
          <span className="label-overline">Signups · last 30 days</span>
        </div>
        <span className="font-mono-label text-[11px] text-ash/70">
          peak {max.toLocaleString()}/day
        </span>
      </div>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="w-full h-32 block"
        preserveAspectRatio="none"
        role="img"
        aria-label="Signups per day for the last 30 days"
      >
        {daily.map((d, i) => {
          const h = Math.round((d.count / max) * (H - pad * 2));
          const x = pad + i * barW + 1;
          const y = H - pad - h;
          const isToday = i === daily.length - 1;
          return (
            <g key={d.date}>
              <rect
                x={x}
                y={y}
                width={Math.max(1, barW - 2)}
                height={Math.max(1, h)}
                rx={1.5}
                fill={isToday ? "#5B9EC9" : "rgba(226,222,208,0.45)"}
              >
                <title>{`${d.date} · ${d.count}`}</title>
              </rect>
            </g>
          );
        })}
        {/* Baseline */}
        <line
          x1={pad}
          x2={W - pad}
          y1={H - pad}
          y2={H - pad}
          stroke="rgba(255,255,255,0.08)"
          strokeWidth={1}
        />
      </svg>
      <div className="mt-3 flex items-center justify-between font-mono-label text-[10px] text-ash/60 tracking-wider">
        <span>{daily[0]?.date}</span>
        <span>{daily[daily.length - 1]?.date}</span>
      </div>
    </div>
  );
};

const PageviewChart = ({ daily }) => {
  if (!daily?.length) return null;
  const max = Math.max(1, ...daily.map((d) => Math.max(d.views || 0, d.unique || 0)));
  const W = 720;
  const H = 120;
  const pad = 8;
  const barW = (W - pad * 2) / daily.length;
  const innerW = Math.max(2, barW - 2);
  const halfW = Math.max(1, innerW / 2 - 1);

  return (
    <div data-testid="admin-pageviews-chart" className="glass-card rounded-2xl p-5">
      <div className="flex items-baseline justify-between mb-4">
        <div className="flex items-center gap-2">
          <Eye className="w-3.5 h-3.5 text-bone" />
          <span className="label-overline">Page views · last 30 days</span>
        </div>
        <div className="flex items-center gap-3 font-mono-label text-[10px] text-ash/70 tracking-wider">
          <span className="inline-flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-sm bg-[#5B9EC9]" /> views
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-sm bg-[#5B9EC9]/40" /> unique
          </span>
        </div>
      </div>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="w-full h-32 block"
        preserveAspectRatio="none"
        role="img"
        aria-label="Page views per day for the last 30 days"
      >
        {daily.map((d, i) => {
          const vH = Math.round(((d.views || 0) / max) * (H - pad * 2));
          const uH = Math.round(((d.unique || 0) / max) * (H - pad * 2));
          const x = pad + i * barW + 1;
          return (
            <g key={d.date}>
              <rect
                x={x}
                y={H - pad - vH}
                width={halfW}
                height={Math.max(1, vH)}
                rx={1.5}
                fill="#5B9EC9"
                opacity={0.95}
              >
                <title>{`${d.date} · ${d.views || 0} views`}</title>
              </rect>
              <rect
                x={x + halfW + 1}
                y={H - pad - uH}
                width={halfW}
                height={Math.max(1, uH)}
                rx={1.5}
                fill="rgba(226,222,208,0.4)"
              >
                <title>{`${d.date} · ${d.unique || 0} unique`}</title>
              </rect>
            </g>
          );
        })}
        <line
          x1={pad}
          x2={W - pad}
          y1={H - pad}
          y2={H - pad}
          stroke="rgba(255,255,255,0.08)"
          strokeWidth={1}
        />
      </svg>
      <div className="mt-3 flex items-center justify-between font-mono-label text-[10px] text-ash/60 tracking-wider">
        <span>{daily[0]?.date}</span>
        <span>{daily[daily.length - 1]?.date}</span>
      </div>
    </div>
  );
};

const CountryList = ({ countries, unknown, title = "Top countries", testid = "admin-countries" }) => {
  if (!countries?.length && !unknown) return null;
  const total = countries.reduce((s, c) => s + c.count, 0) + (unknown || 0);
  return (
    <div data-testid={testid} className="glass-card rounded-2xl p-5">
      <div className="flex items-center gap-2 mb-4">
        <Globe2 className="w-3.5 h-3.5 text-bone" />
        <span className="label-overline">{title}</span>
      </div>
      {countries.length === 0 ? (
        <p className="text-ash text-[13px]">
          No country data yet — new signups will be geo-tagged automatically.
        </p>
      ) : (
        <ul className="space-y-2.5">
          {countries.map((c) => {
            const pct = total > 0 ? (c.count / total) * 100 : 0;
            return (
              <li
                key={`${c.code || "x"}-${c.name}`}
                data-testid={`admin-country-${c.code || c.name}`}
                className="flex items-center gap-3"
              >
                <span className="w-8 font-mono-label text-[11px] text-ash/70 uppercase">
                  {c.code || "—"}
                </span>
                <span className="flex-1 text-pearl text-[13px] truncate">{c.name}</span>
                <span className="flex-1 max-w-[200px] h-1 rounded-full bg-[var(--w-border)] overflow-hidden">
                  <span
                    className="block h-full bg-[#5B9EC9]"
                    style={{ width: `${Math.max(4, pct)}%` }}
                  />
                </span>
                <span className="w-12 text-right font-mono-label text-[11px] text-ash">
                  {c.count.toLocaleString()}
                </span>
              </li>
            );
          })}
          {unknown > 0 && (
            <li className="flex items-center gap-3 pt-2 border-t border-[var(--w-border)]">
              <span className="w-8 font-mono-label text-[11px] text-ash/40 uppercase">—</span>
              <span className="flex-1 text-ash/60 text-[13px]">Unknown</span>
              <span className="w-12 text-right font-mono-label text-[11px] text-ash/60">
                {unknown.toLocaleString()}
              </span>
            </li>
          )}
        </ul>
      )}
    </div>
  );
};

const REFERRER_LABELS = {
  "t.co": "X / Twitter",
  "twitter.com": "X / Twitter",
  "x.com": "X / Twitter",
  "lnkd.in": "LinkedIn",
  "linkedin.com": "LinkedIn",
  "reddit.com": "Reddit",
  "old.reddit.com": "Reddit",
  "out.reddit.com": "Reddit",
  "facebook.com": "Facebook",
  "l.facebook.com": "Facebook",
  "fb.com": "Facebook",
  "instagram.com": "Instagram",
  "l.instagram.com": "Instagram",
  "youtube.com": "YouTube",
  "youtu.be": "YouTube",
  "news.ycombinator.com": "Hacker News",
  "hn.algolia.com": "Hacker News",
  "producthunt.com": "Product Hunt",
  "ph.co": "Product Hunt",
  "google.com": "Google",
  "bing.com": "Bing",
  "duckduckgo.com": "DuckDuckGo",
  "tiktok.com": "TikTok",
  "discord.com": "Discord",
  "medium.com": "Medium",
  "substack.com": "Substack",
};

const labelFor = (host) => REFERRER_LABELS[host] || host;

const ReferrerList = ({ referrers, direct }) => {
  const total = (referrers?.reduce((s, r) => s + r.count, 0) || 0) + (direct || 0);
  if (total === 0) return null;
  return (
    <div data-testid="admin-referrers" className="glass-card rounded-2xl p-5">
      <div className="flex items-center gap-2 mb-4">
        <Compass className="w-3.5 h-3.5 text-bone" />
        <span className="label-overline">Top traffic sources</span>
      </div>
      <ul className="space-y-2.5">
        {(referrers || []).map((r) => {
          const pct = total > 0 ? (r.count / total) * 100 : 0;
          const label = labelFor(r.host);
          const showHost = label !== r.host;
          return (
            <li
              key={r.host}
              data-testid={`admin-referrer-${r.host}`}
              className="flex items-center gap-3"
            >
              <span className="flex-1 min-w-0 truncate text-pearl text-[13px]">
                {label}
                {showHost && (
                  <span className="ml-2 text-ash/50 text-[11px] font-mono-label">
                    {r.host}
                  </span>
                )}
              </span>
              <span className="flex-1 max-w-[160px] h-1 rounded-full bg-[var(--w-border)] overflow-hidden">
                <span
                  className="block h-full bg-[#5B9EC9]"
                  style={{ width: `${Math.max(4, pct)}%` }}
                />
              </span>
              <span className="w-12 text-right font-mono-label text-[11px] text-ash">
                {r.count.toLocaleString()}
              </span>
            </li>
          );
        })}
        {direct > 0 && (
          <li className="flex items-center gap-3 pt-2 border-t border-[var(--w-border)]">
            <span className="flex-1 text-ash/70 text-[13px]">
              Direct
              <span className="ml-2 text-ash/50 text-[11px] font-mono-label">
                no referrer
              </span>
            </span>
            <span className="flex-1 max-w-[160px] h-1 rounded-full bg-[var(--w-border)] overflow-hidden">
              <span
                className="block h-full bg-ash/40"
                style={{
                  width: `${Math.max(4, (direct / total) * 100)}%`,
                }}
              />
            </span>
            <span className="w-12 text-right font-mono-label text-[11px] text-ash">
              {direct.toLocaleString()}
            </span>
          </li>
        )}
      </ul>
    </div>
  );
};

export default function AdminAnalytics({ passcode, refreshKey = 0 }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    adminAnalytics(passcode)
      .then((d) => {
        if (alive) {
          setData(d);
          setError("");
          setLoading(false);
        }
      })
      .catch((err) => {
        if (alive) {
          setError(err?.response?.data?.detail || "Couldn't load analytics.");
          setLoading(false);
        }
      });
    return () => {
      alive = false;
    };
  }, [passcode, refreshKey]);

  if (loading && !data) {
    return (
      <div
        data-testid="admin-analytics-loading"
        className="glass-card rounded-2xl p-6 flex items-center gap-3 text-ash text-[13px]"
      >
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading analytics…
      </div>
    );
  }
  if (error) {
    return (
      <div
        data-testid="admin-analytics-error"
        className="glass-card rounded-2xl p-6 text-red-300/80 text-[13px]"
      >
        {error}
      </div>
    );
  }
  if (!data) return null;

  return (
    <section data-testid="admin-analytics" className="mb-10 space-y-6">
      <div>
        <div className="label-overline mb-3 text-ash/70">Reservations</div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            label="Total"
            value={data.total}
            hint="all signups"
            testid="admin-stat-total"
          />
          <StatCard
            label="Today"
            value={data.today}
            hint="reservations today"
            testid="admin-stat-today"
          />
          <StatCard
            label="Last 7d"
            value={data.last_7d}
            hint="rolling 7-day window"
            testid="admin-stat-7d"
          />
          <StatCard
            label="Last 30d"
            value={data.last_30d}
            hint="rolling 30-day window"
            testid="admin-stat-30d"
          />
        </div>
      </div>

      <div className="grid grid-cols-12 gap-5">
        <div className="col-span-12 lg:col-span-7">
          <SignupChart daily={data.daily} />
        </div>
        <div className="col-span-12 lg:col-span-5">
          <CountryList countries={data.countries} unknown={data.unknown_country} />
        </div>
      </div>

      {data.pageviews && (
        <>
          <div className="pt-2">
            <div className="label-overline mb-3 text-ash/70">Page views</div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard
                label="Total views"
                value={data.pageviews.total}
                hint="all-time"
                testid="admin-pv-total"
              />
              <StatCard
                label="Unique visitors"
                value={data.pageviews.unique_total}
                hint="all-time (daily unique)"
                testid="admin-pv-unique"
              />
              <StatCard
                label="Today"
                value={data.pageviews.today}
                hint={`${data.pageviews.unique_today.toLocaleString()} unique today`}
                testid="admin-pv-today"
              />
              <StatCard
                label="Unique · 7d"
                value={data.pageviews.unique_last_7d}
                hint="rolling 7-day window"
                testid="admin-pv-unique-7d"
              />
            </div>
          </div>

          <div className="grid grid-cols-12 gap-5">
            <div className="col-span-12 lg:col-span-7">
              <PageviewChart daily={data.pageviews.daily} />
            </div>
            <div className="col-span-12 lg:col-span-5">
              <CountryList
                countries={data.pageviews.countries}
                unknown={data.pageviews.unknown_country}
                title="Top visitor countries"
                testid="admin-pv-countries"
              />
            </div>
          </div>

          <ReferrerList
            referrers={data.pageviews.referrers}
            direct={data.pageviews.direct}
          />
        </>
      )}

      <div className="flex items-center gap-2 text-[11px] text-ash/60">
        <Calendar className="w-3 h-3" />
        <span className="font-mono-label tracking-wider">
          Updated {new Date().toLocaleTimeString()}
        </span>
      </div>
    </section>
  );
}
